import { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@Sage/hoi-poi-ui';

import { FLAGSMITH_FLAGS_TO_CONFIG } from 'constants/Features';
import DevTools, { DevToolsSwitchRow, DevToolsTabTitle } from '@Sage/ssm-web5/components/DevTools';

import {
    isEmbedSandboxAvailable,
    isEmbedSandboxEnabled,
    setEmbedSandboxEnabled,
} from './embedSandbox';
import { isDevEnvironmentSwitcherEnabled } from './environments';
import {
    clearFlagOverrides,
    getFlagOverrides,
    hasFlagOverrides,
    setFlagOverride,
} from './flagsmithOverrides';
import {
    isLocalFormFragmentAvailable,
    isLocalFormFragmentEnabled,
    setLocalFormFragmentEnabled,
} from './localFormFragment';

const TAB_CUSTOM = 'custom';
const TAB_FLAGSMITH = 'flagsmith';

const DevPopUp = memo(() => {
    const flagsmith = useSelector((state) => state.config?.flagsmith || {});
    const flagOverrides = useMemo(() => getFlagOverrides(), []);
    const hasFlagsOverride = useMemo(() => hasFlagOverrides(), []);
    const showEmbedSandbox = isEmbedSandboxAvailable();
    const hasEmbedSandboxOverride = useMemo(() => isEmbedSandboxEnabled(), []);
    const showLocalFormFragment = isLocalFormFragmentAvailable();
    const hasLocalFormFragmentOverride = useMemo(() => isLocalFormFragmentEnabled(), []);
    const hasExtraOverride =
        hasFlagsOverride || hasEmbedSandboxOverride || hasLocalFormFragmentOverride;
    const handleFlagChange = useCallback((key, checked) => {
        setFlagOverride(key, checked);
        window.location.reload();
    }, []);
    const handleClearFlagOverrides = useCallback(() => {
        clearFlagOverrides();
        window.location.reload();
    }, []);
    const handleEmbedSandboxChange = useCallback((checked) => {
        setEmbedSandboxEnabled(checked);
        window.location.reload();
    }, []);
    const handleLocalFormFragmentChange = useCallback((checked) => {
        setLocalFormFragmentEnabled(checked);
        window.location.reload();
    }, []);
    const extraTabs = useMemo(
        () => [
            {
                key: TAB_FLAGSMITH,
                title: <DevToolsTabTitle icon="flag" label="Flagsmith" />,
                content: (
                    <div className="fm-dev__popup__flags">
                        <div className="fm-dev__popup__flags__list">
                            {FLAGSMITH_FLAGS_TO_CONFIG.map((flag) => (
                                <DevToolsSwitchRow
                                    key={flag}
                                    checked={Boolean(flagsmith[flag])}
                                    isOverridden={Object.prototype.hasOwnProperty.call(
                                        flagOverrides,
                                        flag,
                                    )}
                                    label={flag}
                                    onChange={(checked) => handleFlagChange(flag, checked)}
                                />
                            ))}
                        </div>
                        <Button
                            size="small"
                            type="secondary"
                            isDisabled={!hasFlagsOverride}
                            onClick={handleClearFlagOverrides}
                        >
                            Clear overrides
                        </Button>
                    </div>
                ),
            },
            {
                key: TAB_CUSTOM,
                title: <DevToolsTabTitle icon="sandbox" label="Custom" />,
                content: (
                    <div className="fm-dev__popup__flags">
                        <div className="fm-dev__popup__flags__list">
                            {showEmbedSandbox ? (
                                <DevToolsSwitchRow
                                    checked={hasEmbedSandboxOverride}
                                    description="Hosts Sales Orders CRUD at /ssm-embed/example.html (same Web4 dev server) and skips POST /api/external/v1/forms."
                                    isOverridden={hasEmbedSandboxOverride}
                                    label="Sales Orders SDK sandbox"
                                    onChange={handleEmbedSandboxChange}
                                />
                            ) : null}
                            {showLocalFormFragment ? (
                                <DevToolsSwitchRow
                                    checked={hasLocalFormFragmentOverride}
                                    description="Loads the Iberfluid form iframe from https://localhost:3010 (npm run start:web4 in iberfluid-form). Trust that HTTPS cert once in the browser."
                                    isOverridden={hasLocalFormFragmentOverride}
                                    label="Local Iberfluid form"
                                    onChange={handleLocalFormFragmentChange}
                                />
                            ) : null}
                        </div>
                    </div>
                ),
            },
        ],
        [
            flagOverrides,
            flagsmith,
            handleClearFlagOverrides,
            handleEmbedSandboxChange,
            handleFlagChange,
            handleLocalFormFragmentChange,
            hasEmbedSandboxOverride,
            hasFlagsOverride,
            hasLocalFormFragmentOverride,
            showEmbedSandbox,
            showLocalFormFragment,
        ],
    );

    if (!isDevEnvironmentSwitcherEnabled()) return null;

    return <DevTools enabled extraTabs={extraTabs} hasExtraOverride={hasExtraOverride} />;
});

DevPopUp.displayName = 'DevPopUp';

export default DevPopUp;
